import type { CoreError } from '@ingenico/api-sdk-core/common';
import { enumDeploymentRequestTriggerTargetTypeV2 } from '@ingenico/api-sdk-device-management-public-v2';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SyncConfigurationMerchantModal } from '../components/sync-configuration-merchant-modal/SyncConfigurationMerchantModal';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';

export interface UseSyncMerchantConfigurationModalReturn {
  readonly modalComponent: ReactNode;
  readonly handleOpenModal: (merchantId: string) => void;
  readonly isModalOpen: boolean;
  readonly handleCloseModal: () => void;
}

export function useSyncMerchantConfigurationModal(): UseSyncMerchantConfigurationModalReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.MERCHANT]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const deviceManagementBusinessService = useBusinessService(DeviceManagementService);
  const [syncRequestState, setSyncRequestState] = useAsyncDataState<unknown, CoreError>();
  const [merchantId, setMerchantId] = useState<string>('');
  const memorizedMerchantId = useMemo(() => toString(merchantId), [merchantId]);

  const onCancel = useCallback(() => {
    setIsModalOpen(false);
    setSyncRequestState({
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
      data: undefined,
    });
    setMerchantId('');
  }, [setSyncRequestState]);

  const handleOpenModal = useCallback(
    (merchantIdParam: string) => {
      setIsModalOpen(true);
      setMerchantId(merchantIdParam);
    },
    [setIsModalOpen],
  );

  const onSync = useCallback((): void => {
    requestAndUpdateState(
      deviceManagementBusinessService.triggerTargetDeploymentRequests({
        targets: [
          {
            type: enumDeploymentRequestTriggerTargetTypeV2.MERCHANT,
            id: memorizedMerchantId,
          },
        ],
      }),
      setSyncRequestState,
      {
        onSuccess: () => {
          showNotification({
            content: toString(t('success.syncConfiguration')),
            level: Level.Success,
          });
          onCancel();
        },
        onError: () => {
          showNotification({
            content: toString(t('error.default.syncConfiguration')),
            level: Level.Error,
          });
        },
      },
    );
  }, [memorizedMerchantId, onCancel, deviceManagementBusinessService, setSyncRequestState, t]);

  return {
    modalComponent: (
      <SyncConfigurationMerchantModal
        isOpen={isModalOpen}
        isLoading={syncRequestState.isLoading}
        onSync={onSync}
        onCancel={onCancel}
        merchantId={merchantId}
      />
    ),
    handleOpenModal,
    isModalOpen,
    handleCloseModal: onCancel,
  };
}
